import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, BookOpen, Award, Briefcase, ArrowUpRight } from "lucide-react";
import { DESTINATIONS } from "@/lib/data";

export default function Destinations() {
  const [active, setActive] = useState(null);

  return (
    <section id="destinations" data-testid="destinations-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-bold mb-4">Study Destinations</div>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tighter">
              Seven countries. <br />
              <span className="font-serif italic text-gradient-royal">Infinite possibilities.</span>
            </h2>
          </div>
          <p className="text-muted-foreground max-w-md">
            Explore top-tier universities, courses and scholarships across the world's most sought-after study destinations.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {DESTINATIONS.map((d, i) => (
            <motion.button
              key={d.country}
              onClick={() => setActive(d)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`destination-card-${d.short.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative overflow-hidden rounded-3xl aspect-[3/4] text-left"
            >
              <img
                src={d.image}
                alt={`Study in ${d.country}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute top-5 right-5 w-10 h-10 rounded-full glass flex items-center justify-center text-white group-hover:bg-[#D4AF37] group-hover:border-[#D4AF37] transition-colors">
                <ArrowUpRight className="w-4 h-4" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-xs tracking-[0.25em] uppercase text-[#FCD34D] mb-2">Study In</div>
                <div className="text-2xl font-heading font-semibold">{d.country}</div>
                <div className="mt-2 text-xs text-white/70">{d.career}</div>
              </div>
            </motion.button>
          ))}

          {/* CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-3xl aspect-[3/4] p-6 flex flex-col justify-between bg-gradient-to-br from-[#0F3D8C] to-[#0A1F4C] text-white"
          >
            <div>
              <div className="text-xs tracking-[0.25em] uppercase text-[#FCD34D] mb-3">Not Sure?</div>
              <div className="text-2xl font-heading font-light leading-tight">
                Let our counsellors help you choose the <span className="font-serif italic text-[#FCD34D]">perfect country</span>.
              </div>
            </div>
            <button
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="destinations-cta-btn"
              className="mt-4 inline-flex items-center gap-2 self-start bg-[#D4AF37] text-[#0F172A] rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#F4C842] transition-colors"
            >
              Free Counselling
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="destination-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-3xl w-full glass-solid rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
            >
              <div className="relative h-56 md:h-72">
                <img src={active.image} alt={active.country} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="absolute bottom-4 left-6 text-white">
                  <div className="text-xs tracking-[0.25em] uppercase text-[#FCD34D]">Study In</div>
                  <div className="text-4xl font-heading font-light">{active.country}</div>
                </div>
                <button onClick={() => setActive(null)} data-testid="destination-modal-close" className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20">✕</button>
              </div>
              <div className="p-6 md:p-8 grid sm:grid-cols-2 gap-6">
                <Detail icon={GraduationCap} title="Top Universities" items={active.universities} />
                <Detail icon={BookOpen} title="Popular Courses" items={active.courses} />
                <Detail icon={Award} title="Scholarships" text={active.scholarships} />
                <Detail icon={Briefcase} title="Career Opportunities" text={active.career} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function Detail({ icon: Icon, title, items, text }) {
  return (
    <div className="p-4 rounded-2xl bg-secondary/50 border border-border">
      <div className="flex items-center gap-2 text-[#0F3D8C] dark:text-[#60A5FA] mb-2">
        <Icon className="w-4 h-4" />
        <div className="text-xs tracking-[0.2em] uppercase font-bold">{title}</div>
      </div>
      {items ? (
        <ul className="text-sm space-y-1">
          {items.map((it) => <li key={it}>• {it}</li>)}
        </ul>
      ) : (
        <div className="text-sm">{text}</div>
      )}
    </div>
  );
}
